import { ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { useIntelligentWeb } from '../../../context/IntelligentWebContext.jsx'
import { useForm } from '../../../context/FormContext.jsx'
import { useOrderStatus } from '../../../context/OrderStatusContext.jsx'
import { cn } from '../../../lib/cn.js'
import { fetchWidgetLeadFormById } from '../../../lib/leadFormsApi.js'

/**
 * Quick-reply chips rendered inside a bot bubble (citations, menu, etc.).
 * Shared with {@link ButtonsMessage} for BotMan `buttons` payloads.
 */
export function MessageQuickReplies({
  buttons,
  messageId = 'msg',
  conf,
  ochatbot,
  className,
}) {
  const { activeLeadForms, setActiveLeadForms } = useIntelligentWeb()
  const { openForm } = useForm()
  const { openOrderStatus } = useOrderStatus()

  if (!Array.isArray(buttons) || buttons.length === 0) return null

  const performAction = async (button) => {
    if (button.type === 'web_url') {
      const match = button.url?.match(/^script:(.*):script:id:(.*):id/) || []
      if (match.length >= 3) {
        ochatbot?.callAPI(
          `ometrics-button-click~script-intent-${match[2]}`,
          button.title,
          true,
        )
        try {
          new Function(match[1])()
        } catch (err) {
          console.error('MessageQuickReplies: script execution error', err)
        }
        return
      }
      ochatbot?.callAPI(
        `ometrics-button-click~${button.url}`,
        button.title,
        true,
      )
      if (button.url) window.open(button.url, '_blank', 'noopener')
      return
    }

    if (
      button.payload === 'order-status-open' ||
      String(button.payload || '').startsWith('order-status-open')
    ) {
      openOrderStatus()
      ochatbot?.callAPI(
        'ometrics-button-click~order-status-open',
        button.title,
        true,
      )
      return
    }

    if (button.payload?.startsWith('lead-form-trigger-')) {
      const formId = button.payload.replace('lead-form-trigger-', '')
      const form = activeLeadForms.find(
        (f) => String(f.id) === String(formId),
      )
      if (form) {
        openForm(form)
        ochatbot?.callAPI(
          `ometrics-button-click~lead-form-trigger-${formId}`,
          button.title,
          true,
        )
        return
      }

      try {
        const list = await fetchWidgetLeadFormById({
          apiBaseUrl: conf?.apiBaseUrl,
          agentId: conf?.agentId || conf?.chatId,
          masterKey: conf?.masterKey,
          formId,
        })
        const fetched = Array.isArray(list)
          ? list.find((f) => String(f.id) === String(formId)) || list[0]
          : null
        if (!fetched) {
          toast('This form is unavailable right now.')
          return
        }
        setActiveLeadForms?.((prev) => {
          const arr = Array.isArray(prev) ? prev : []
          if (arr.some((f) => String(f.id) === String(fetched.id))) return arr
          return [...arr, fetched]
        })
        openForm(fetched)
        ochatbot?.callAPI(
          `ometrics-button-click~lead-form-trigger-${formId}`,
          button.title,
          true,
        )
        return
      } catch (err) {
        console.error('Lead form lookup failed', err)
        toast('Could not load the form. Please try again.')
        return
      }
    }

    ochatbot?.callAPI(button.payload || button.title, button.title, true)
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {buttons.map((button, index) => (
        <button
          key={`${messageId}-btn-${index}`}
          type="button"
          onClick={() => void performAction(button)}
          style={{
            backgroundColor: conf?.widgetButtonColor || '#0D870D',
            color: conf?.widgetButtonTextColor || '#ffffff',
          }}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm',
            'transition-colors hover:brightness-95',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          )}
        >
          {button.type === 'web_url' && (
            <ExternalLink className="h-3.5 w-3.5" />
          )}
          <span>{button.title}</span>
        </button>
      ))}
    </div>
  )
}
